import {
  IContactFormDataForAPI,
  IContactPayloadForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import { UserRole } from 'src/SharedCustomHooks/UseSuiteActions/types';

const isServiceProviderOrVendor = (role: string): boolean => {
  return role === 'Service Provider' || role === 'Vendor';
};

const isHomeOwnerOrPropertyDeveloper = (role: string): boolean => {
  return role === 'Home Owner' || role === 'Property Developer';
};

const formatPhoneNumber = (phone: string): string => {
  const cleaned = ('' + phone).replace(/\D/g, '');

  return cleaned.length === 10
    ? cleaned.replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3')
    : cleaned;
};

const formatName = (name: string): string => {
  if (!name) return '';

  return name.charAt(0).toUpperCase() + name.slice(1);
};

const getAssignedRoles = (
  userRole: string,
  allRoles: UserRole[] | null | undefined,
  fallbackRoles?: any[]
): string[] => {
  if (userRole && allRoles?.length) {
    const matchingRole = allRoles.find(
      (role: UserRole) => userRole === role.name
    );

    return matchingRole ? [matchingRole.id] : [];
  }

  if (fallbackRoles?.[0]?.role?.id) {
    return [fallbackRoles[0].role.id];
  }

  return [];
};

// Always send the contact's current profileImage. The contact PUT (UpdateUser)
// does `user.ProfileImage = model.ProfileImage` unconditionally, so sending
// undefined/null wipes the stored image. On edit, formData.profileImage holds
// the existing URL (loaded from the contact); on create it's empty. By the time
// this runs any upload has already resolved into formData.profileImage.
// TODO(brittle-api): this only stays safe because we never drop the field —
// the real fix is a null-guarded partial update on UpdateUser so the FE can
// omit profileImage entirely without nuking it.
const getProfileImageForPayload = (
  formData: IContactFormDataForAPI
): string | undefined => {
  return formData.profileImage;
};

const getSuiteIdForPayload = (
  formData: IContactFormDataForAPI
): string | null => {
  return isServiceProviderOrVendor(formData.userRole)
    ? null
    : formData.suiteId || null;
};

const getPropertyIdForPayload = (
  formData: IContactFormDataForAPI
): string | null => {
  return formData.userRole === 'Service Provider' ||
    formData.userRole === 'Vendor'
    ? null
    : formData.propertyId || null;
};

const createBasePayload = (
  formData: IContactFormDataForAPI,
  userId: string,
  allRoles: UserRole[] | null | undefined,
  pageSubmittedFrom?: string
): IContactPayloadForAPI => {
  const {
    firstname,
    lastname,
    email,
    phone,
    userRole,
    address,
  }: IContactFormDataForAPI = formData;

  const formattedNumber: string = formatPhoneNumber(phone);
  const formattedFirstname: string = formatName(firstname);
  const assignedRoles: string[] = getAssignedRoles(userRole, allRoles, formData.roles);

  const basePayload: any = {
    username: email,
    email,
    firstname: formattedFirstname,
    lastname,
    phone: formattedNumber,
    profileImage: getProfileImageForPayload(formData),
    languages: null,
    customerId: null,
    deleted: false,
    assignedRoles,
    address,
    UserId: userId,
    SendActivationLink: formData.sendActivationEmail || false,
    IsMultiSelect: true,
    RoleName: userRole,
    RoleId: assignedRoles[0],
    CompanyName: formData.companyName,
    isDashboardAccess: formData.isDashboardAccess || false,
    PageSubmittedFrom: pageSubmittedFrom,
  };

  // Add ID for updates or SentEmail for new contacts.
  if (formData.id) {
    basePayload.id = formData.id;
  } else {
    basePayload.SentEmail = true;
  }

  return basePayload;
};

const addServiceProviderFields = (
  payload: Partial<any>,
  formData: IContactFormDataForAPI
): Partial<IContactPayloadForAPI> => {
  if (
    formData.userRole === 'Service Provider' ||
    formData.userRole === 'Vendor'
  ) {
    const servicesProvidedFromForm = formData.servicesProvided;

    return {
      ...payload,
      ServicesProvided: servicesProvidedFromForm,
      // CRITICAL: Always include ServiceProviderAssignedProperties for Service Providers!
      ...(formData.userRole === 'Service Provider' && {
        ServiceProviderAssignedProperties:
          formData.ServiceProviderAssignedProperties || [],
      }),
    };
  }

  return payload;
};

const addHomePropertyFields = (
  payload: Partial<IContactPayloadForAPI>,
  formData: IContactFormDataForAPI
): Partial<IContactPayloadForAPI> => {
  if (!isHomeOwnerOrPropertyDeveloper(formData.userRole)) {
    return payload;
  }

  const result: any = {
    ...payload,
    Suites: formData.Suites?.map((suite: any) => ({
      Id: suite.Id,
      PropertyId: suite.PropertyId || suite.propertyId,
    })),
    Properties: formData.properties,
    Designated:
      formData.userRole === 'Home Owner'
        ? formData.designated || false
        : undefined,
  };

  // Add designated flag for Home Owners only
  if (formData.userRole === 'Home Owner') {
    result.Designated = formData.designated || false;
  }

  return result;
};

const addRealtorFields = (
  payload: Partial<any>,
  formData: IContactFormDataForAPI
): Partial<IContactPayloadForAPI> => {
  if (formData.userRole === 'Realtor') {
    return {
      ...payload,
      Suites: formData.Suites?.map((suite: any) => ({
        Id: suite.Id,
        PropertyId: suite.PropertyId || suite.propertyId,
      })),
      Properties: formData.properties,
    };
  }

  return payload;
};

const addOccupantFields = (
  payload: Partial<any>,
  formData: IContactFormDataForAPI
): Partial<IContactPayloadForAPI> => {
  if (formData.userRole === 'Occupant') {
    return {
      ...payload,
      SuiteId: getSuiteIdForPayload(formData),
      PropertyId: getPropertyIdForPayload(formData),
    };
  }

  return payload;
};

const addFieldsForUsersThatNeedACompanyName = (
  payload: Partial<any>,
  formData: IContactFormDataForAPI
) => {
  if (
    formData.userRole === 'Strata Manager' ||
    formData.userRole === 'Vendor' ||
    formData.userRole === 'Service Provider'
  ) {
    return {
      ...payload,
      CompanyName: formData.companyName,
    };
  }

  // Explicitly remove CompanyName for all other roles.
  const { CompanyName, ...payloadWithoutCompanyName } = payload;
  return payloadWithoutCompanyName;
};

const addServiceManagerFields = (
  payload: Partial<any>,
  formData: IContactFormDataForAPI
): Partial<IContactPayloadForAPI> => {
  if (formData.userRole === 'Service Manager') {
    return {
      ...payload,
      PropertyId: getPropertyIdForPayload(formData),
    };
  }

  return payload;
};

// Helper to add calendar and personal info fields
const addCalendarFields = (
  payload: Partial<any>,
  formData: IContactFormDataForAPI
): Partial<IContactPayloadForAPI> => {
  if (
    formData.userRole === 'Service Provider' ||
    formData.userRole === 'Property Developer'
  ) {
    return {
      ...payload,
      CalendarLink: formData.calendarLink?.trim() || '',
      PersonalEmail: formData.personalEmail || '',
    };
  }

  return payload;
};

export {
  addCalendarFields,
  addServiceManagerFields,
  addOccupantFields,
  addRealtorFields,
  addHomePropertyFields,
  addServiceProviderFields,
  createBasePayload,
  getPropertyIdForPayload,
  getSuiteIdForPayload,
  getProfileImageForPayload,
  getAssignedRoles,
  formatName,
  formatPhoneNumber,
  addFieldsForUsersThatNeedACompanyName,
};
